import { Badge, Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { useCallback } from 'react';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { constructQueryParams } from '@duke-hq/lib';
import { useParams, useNavigate } from 'react-router-dom';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import type { SortFilters } from './RegionsLocations';
import { LocationError } from './LocationError';
import { SortOrder } from '@duke-hq/lib/pagination';

type RegionLocationItemType =
  RouterOutputs['providers']['locations']['listSiteRegionsWithPagination']['items'][number];

export function RegionsLocationsTable({
  regions,
  urlParams,
  onFilters,
}: {
  regions: RegionLocationItemType[];
  urlParams: SortFilters;
  onFilters: (filters: SortFilters) => void;
}) {
  const { providerId } = useParams<{ providerId: string }>();
  const { sort, orderBy } = urlParams;
  const navigate = useNavigate();

  const handleClick = useCallback(
    (id: string) => {
      if (providerId != null) {
        const { page, orderBy, sort } = urlParams;
        const baseUrl = `/providers/${providerId}/locations/region/${id}`;
        navigate(`${baseUrl}${constructQueryParams({ page, orderBy, sort })}`);
      }
    },
    [providerId, urlParams, navigate],
  );

  const handleSort = useCallback(
    (orderBy: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      onFilters({ ...urlParams, sort: newSortDirection, orderBy });
    },
    [onFilters, sort, urlParams],
  );

  if (providerId == null) {
    return <LocationError message={t`An error occurred, providerId is required.`} />;
  }

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Name`}
              columnName="name"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Text>{t`Primary`}</Text>
          </Th>
          <Th>
            <Text>{t`Description`}</Text>
          </Th>
          <Th>
            <Text>{t`Sites Available`}</Text>
          </Th>
          <Th>
            <Text>{t`Consultants`}</Text>
          </Th>
          <Th>
            <Text>{t`Enrolments`}</Text>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(regions) && regions.length
          ? regions.map((region: RegionLocationItemType) => (
              <TableRow
                key={region.id}
                region={region}
                onRegion={(id: string) => {
                  handleClick(id);
                }}
              />
            ))
          : null}
      </Tbody>
    </Table>
  );
}

function TableRow({
  region,
  onRegion,
}: {
  region: RegionLocationItemType;
  onRegion: (id: string) => void;
}) {
  return (
    <Tr
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
      onClick={() => {
        if (region?.id) {
          onRegion(region.id);
        }
      }}
    >
      <Td>
        <Flex direction="column">
          <Box>
            <Text fontWeight="medium">{region.name}</Text>
          </Box>
        </Flex>
      </Td>
      <Td>
        {region.is_default ? (
          <Badge variant="solid" colorScheme="brand" textTransform="none">
            {t`Primary`}
          </Badge>
        ) : null}
      </Td>
      <Td>
        <Box>
          <Text fontWeight="medium">{region.description}</Text>
        </Box>
      </Td>
      <Td>{region.totalSites || 0}</Td>
      <Td>{region.totalConsultants || 0}</Td>
      <Td>{region.totalEnrolments || 0}</Td>
    </Tr>
  );
}
